import { useState, useRef, useEffect } from "react";
import LessonSummaryModal from "./LessonSummaryModal";
import YouTubeTranscriber from "./YouTubeTranscriber";
import TranscriptionHistory from "./TranscriptionHistory";
import { useTranscriptionHistory } from "../hooks/useTranscriptionHistory";
import { getAuth } from "firebase/auth";
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";

// ── No API key on the client — all AssemblyAI calls go through Cloud Functions ──
const FUNCTIONS_BASE_URL = "https://us-central1-kings-app-74847.cloudfunctions.net";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "pt", label: "Portuguese" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "zh", label: "Chinese" },
  { code: "ar", label: "Arabic" },
];

const RTL_LANGUAGES = ["ar", "he", "fa", "ur"];

// Darkened for legibility on a light background
const SPEAKER_COLORS = [
  "#2563EB", "#059669", "#D97706", "#DB2777",
  "#7C3AED", "#EA580C", "#0891B2", "#16A34A",
];

const VIDEO_TYPES = [
  "video/mp4", "video/quicktime", "video/webm",
  "video/x-matroska", "video/x-msvideo", "video/x-m4v",
];

function formatTime(ms) {
  const totalSec = Math.floor(ms / 1000);
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export default function AudioTranscriber() {
  const [mode, setMode] = useState("upload");
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState("en");
  const [secondLanguage, setSecondLanguage] = useState("");   // "" = no second language
  const [showSecondLang, setShowSecondLang] = useState(false);
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [transcript, setTranscript] = useState(null);
  const [error, setError] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [videoDragOver, setVideoDragOver] = useState(false);
  const [activeTab, setActiveTab] = useState("full");
  const [showSummary, setShowSummary] = useState(false);
  const [savedEntry, setSavedEntry] = useState(null);
  const [textInput, setTextInput] = useState("");
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [videoStatus, setVideoStatus] = useState("idle");
  const [videoProgress, setVideoProgress] = useState(0);
  const textFileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);

  const { history, loading: historyLoading, saving, saveTranscription, saveSummary, deleteTranscription } = useTranscriptionHistory();

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Derived
  const isVideoProcessing = videoStatus === "uploading" || videoStatus === "extracting";
  const isProcessing = status === "uploading" || status === "processing" || isVideoProcessing;
  const isRTL = RTL_LANGUAGES.includes(language);
  // Languages to send to CF — array if dual, single string if not
  const languagePayload = showSecondLang && secondLanguage
    ? [language, secondLanguage]
    : language;

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && isRecording && !wakeLockRef.current) {
        if ("wakeLock" in navigator) {
          try { wakeLockRef.current = await navigator.wakeLock.request("screen"); }
          catch (err) { console.warn("Wake lock re-acquire failed:", err); }
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isRecording]);

  useEffect(() => {
    loadAudioDevices();
    navigator.mediaDevices?.addEventListener("devicechange", loadAudioDevices);
    return () => navigator.mediaDevices?.removeEventListener("devicechange", loadAudioDevices);
  }, []);

  const loadAudioDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter((d) => d.kind === "audioinput");
      setAudioDevices(inputs);
      if (selectedDeviceId && !inputs.find((d) => d.deviceId === selectedDeviceId)) {
        setSelectedDeviceId("");
      }
    } catch (err) {
      console.warn("Could not enumerate audio devices:", err);
    }
  };

  const startRecording = async () => {
    if ("wakeLock" in navigator) {
      try { wakeLockRef.current = await navigator.wakeLock.request("screen"); }
      catch (err) { console.warn("Wake lock failed:", err); }
    }
    try {
      const audioConstraints = selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : true;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      loadAudioDevices();
      const mimeType = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg']
        .find(type => MediaRecorder.isTypeSupported(type)) || '';
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        const ext = mediaRecorder.mimeType.includes('mp4') ? 'm4a'
          : mediaRecorder.mimeType.includes('ogg') ? 'ogg' : 'webm';
        setFile(new File([blob], `recording.${ext}`, { type: mediaRecorder.mimeType }));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorder.start(250);
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch {
      setError("Microphone access denied. Please allow microphone permissions.");
      if (wakeLockRef.current) { wakeLockRef.current.release(); wakeLockRef.current = null; }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) { mediaRecorderRef.current.stop(); setIsRecording(false); clearInterval(timerRef.current); }
    if (wakeLockRef.current) { wakeLockRef.current.release(); wakeLockRef.current = null; }
  };

  const handleFileChange = (f) => {
    if (f && f.type.startsWith("audio/")) {
      setFile(f); setAudioUrl(URL.createObjectURL(f)); setTranscript(null); setError("");
    } else if (f) {
      setError("Please select a valid audio file (MP3, WAV, M4A, WebM, etc.)");
    }
  };

  const handleVideoFileChange = (f) => {
    if (!f) return;
    const isVideo = VIDEO_TYPES.includes(f.type) || f.name.match(/\.(mp4|mov|webm|mkv|avi|m4v)$/i);
    if (isVideo) { setVideoFile(f); setError(""); }
    else { setError("Please select a valid video file (MP4, MOV, WebM, MKV, AVI, M4V)"); }
  };

  // ── Upload audio → Cloud Function ────────────────────────────
  const uploadAndTranscribe = async () => {
    if (!file) return;
    setStatus("uploading"); setProgress(10); setError("");
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Not authenticated. Please sign in again.");
      const idToken = await user.getIdToken();

      const storage = getStorage();
      const ext = file.name.split(".").pop();
      const storagePath = `audio-uploads/${user.uid}/${Date.now()}.${ext}`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, file);
        uploadTask.on("state_changed",
          (snapshot) => {
            const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 40);
            setProgress(10 + pct);
          },
          reject,
          () => resolve()
        );
      });

      setProgress(50);
      setStatus("processing");

      const res = await fetch(`${FUNCTIONS_BASE_URL}/processAudioFile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ storagePath, language: languagePayload }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Transcription failed.");

      setTranscript(data.transcript);
      setStatus("done");
      setProgress(100);

      const entry = await saveTranscription({ file, transcript: data.transcript, language: languagePayload, source: mode });
      setSavedEntry(entry);
    } catch (err) {
      setError(err.message); setStatus("error");
    }
  };

  const uploadVideoAndTranscribe = async () => {
    if (!videoFile) return;
    setVideoStatus("uploading"); setVideoProgress(0); setError("");
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Not authenticated. Please sign in again.");
      const idToken = await user.getIdToken();

      const storage = getStorage();
      const ext = videoFile.name.split(".").pop();
      const storagePath = `video-uploads/${user.uid}/${Date.now()}.${ext}`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, videoFile);
        uploadTask.on("state_changed",
          (snapshot) => {
            const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setVideoProgress(pct);
          },
          reject,
          () => resolve()
        );
      });

      setVideoStatus("extracting");

      const res = await fetch(`${FUNCTIONS_BASE_URL}/processVideoFile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ storagePath, language: languagePayload }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Video processing failed.");

      setTranscript(data.transcript);
      setStatus("done");
      setVideoStatus("done");

      const entry = await saveTranscription({ file: null, transcript: data.transcript, language: languagePayload, source: "video" });
      setSavedEntry(entry);
    } catch (err) {
      setError(err.message); setVideoStatus("error");
    }
  };

  const reset = () => {
    setFile(null); setAudioUrl(null); setTranscript(null);
    setStatus("idle"); setProgress(0); setError("");
    setRecordingTime(0); setSavedEntry(null); setTextInput("");
    setVideoFile(null); setVideoStatus("idle"); setVideoProgress(0);
  };

  const handleSelectHistory = (entry) => {
    setTranscript(entry.transcript);
    setSavedEntry(entry);
    const lang = entry.language;
    if (Array.isArray(lang)) {
      setLanguage(lang[0]); setSecondLanguage(lang[1] || ""); setShowSecondLang(!!lang[1]);
    } else {
      setLanguage(lang || "en"); setSecondLanguage(""); setShowSecondLang(false);
    }
    setStatus("done"); setMode("upload");
  };

  const getSpeakerColor = (label) => {
    const idx = label?.charCodeAt(label.length - 1) % SPEAKER_COLORS.length || 0;
    return SPEAKER_COLORS[idx];
  };

  const groupBySpeaker = (utterances) => {
    if (!utterances) return [];
    const grouped = [];
    let current = null;
    for (const u of utterances) {
      if (current && current.speaker === u.speaker) { current.text += " " + u.text; current.end = u.end; }
      else { if (current) grouped.push(current); current = { ...u }; }
    }
    if (current) grouped.push(current);
    return grouped;
  };

  const wordGroups = transcript?.words
    ? (() => {
        const groups = []; let buf = [];
        for (const w of transcript.words) {
          buf.push(w);
          if (buf.length >= 8 || w.text?.endsWith(".") || w.text?.endsWith("?") || w.text?.endsWith("!")) {
            groups.push(buf); buf = [];
          }
        }
        if (buf.length) groups.push(buf);
        return groups;
      })()
    : [];

  const MODES = [
    { id: "upload",  label: "📁 UPLOAD" },
    { id: "record",  label: "🎤 RECORD" },
    { id: "text",    label: "📝 TEXT" },
    { id: "video",   label: "🎬 VIDEO" },
    { id: "history", label: `🕘 HISTORY${history.length > 0 ? ` (${history.length})` : ""}` },
  ];

  const handleTextSubmit = () => {
    if (!textInput.trim()) return;
    const t = { text: textInput.trim(), words: [], utterances: [], audio_duration: null };
    setTranscript(t); setStatus("done");
    saveTranscription({ file: null, transcript: t, language: languagePayload, source: "text" });
  };

  const handleTextFileChange = (f) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (e) => setTextInput(e.target.result);
    reader.readAsText(f);
  };

  const getDeviceLabel = (device, index) => device.label || `Microphone ${index + 1}`;

  const videoStatusLabel = videoStatus === "uploading" ? "Uploading video..." : "Extracting audio & transcribing...";
  const videoStatusSub   = videoStatus === "uploading" ? "Sending to secure storage" : "FFmpeg extraction → AssemblyAI transcription";

  // ── Language selector (reused across upload/record/video modes) ──
  const LanguageSelector = () => (
    <div style={{ marginBottom: "20px" }}>
      <div style={{ fontSize: "11px", color: "#475569", marginBottom: "8px", letterSpacing: "0.1em", fontWeight: "600" }}>
        LANGUAGE
      </div>
      <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          style={selectStyle}
        >
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>

        {showSecondLang ? (
          <>
            <div style={{ fontSize: "11px", color: "#64748B" }}>+</div>
            <select
              value={secondLanguage}
              onChange={(e) => setSecondLanguage(e.target.value)}
              style={selectStyle}
            >
              <option value="">Select second language</option>
              {LANGUAGES.filter(l => l.code !== language).map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            <button
              onClick={() => { setShowSecondLang(false); setSecondLanguage(""); }}
              style={{
                background: "transparent", border: "1px solid #CBD5E1",
                borderRadius: "6px", padding: "6px 10px", color: "#475569",
                cursor: "pointer", fontSize: "12px", fontFamily: "inherit",
              }}
            >
              ✕
            </button>
          </>
        ) : (
          <button
            onClick={() => setShowSecondLang(true)}
            style={{
              background: "transparent", border: "1px dashed #CBD5E1",
              borderRadius: "6px", padding: "8px 14px", color: "#475569",
              cursor: "pointer", fontSize: "11px", fontFamily: "inherit",
              letterSpacing: "0.06em", whiteSpace: "nowrap",
            }}
          >
            + ADD LANGUAGE
          </button>
        )}
      </div>
      {showSecondLang && secondLanguage && (
        <div style={{ fontSize: "11px", color: "#B45309", marginTop: "8px", letterSpacing: "0.06em", fontWeight: "600" }}>
          ⚡ Bilingual mode — AssemblyAI will detect both languages in the audio
        </div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#F1F5F9", fontFamily: "'Courier New', monospace", color: "#1E293B" }}>

      {/* Header */}
      <div style={{
        borderBottom: "1px solid #E2E8F0", padding: "20px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: "36px", height: "36px", borderRadius: "8px",
            background: "linear-gradient(135deg, #3B82F6, #8B5CF6)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px",
          }}>🎙</div>
          <div>
            <div style={{ fontWeight: "700", fontSize: "16px", letterSpacing: "0.05em", color: "#0F172A" }}>VOICE → TEXT</div>
            <div style={{ fontSize: "11px", color: "#64748B", letterSpacing: "0.1em" }}>AUDIO TRANSCRIPTION ENGINE</div>
          </div>
        </div>
        {saving && <div style={{ fontSize: "11px", color: "#B45309", letterSpacing: "0.08em", fontWeight: "600" }}>💾 SAVING...</div>}
        {savedEntry && !saving && <div style={{ fontSize: "11px", color: "#16A34A", letterSpacing: "0.08em", fontWeight: "600" }}>✓ SAVED</div>}
      </div>

      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "32px 24px" }}>

        {/* Mode Toggle */}
        {!isProcessing && (
          <div style={{
            display: "flex", gap: "4px", background: "#FFFFFF",
            border: "1px solid #E2E8F0", borderRadius: "10px",
            padding: "4px", marginBottom: "24px",
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
          }}>
            {MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => { setMode(m.id); if (m.id !== "history") reset(); }}
                style={{
                  flex: 1, padding: "10px", borderRadius: "7px",
                  border: "none", cursor: "pointer", fontSize: "11px",
                  letterSpacing: "0.06em", fontFamily: "inherit",
                  fontWeight: mode === m.id ? "700" : "600",
                  background: mode === m.id
                    ? m.id === "history" ? "linear-gradient(135deg, #C8973A, #F59E0B)"
                    : m.id === "video"   ? "linear-gradient(135deg, #10B981, #059669)"
                    : "linear-gradient(135deg, #3B82F6, #8B5CF6)"
                    : "transparent",
                  color: mode === m.id ? "#fff" : "#475569",
                  transition: "all 0.2s",
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}

        {/* History Mode */}
        {mode === "history" && (
          <TranscriptionHistory
            history={history} loading={historyLoading}
            onSelect={handleSelectHistory} onDelete={deleteTranscription} onSaveSummary={saveSummary}
          />
        )}

        {/* Text Mode */}
        {mode === "text" && status === "idle" && (
          <div>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", color: "#475569", marginBottom: "8px", letterSpacing: "0.1em", fontWeight: "600" }}>PASTE TRANSCRIPT</div>
              <textarea
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Paste your Zoom, Teams, or other transcript here..."
                rows={10}
                style={{
                  width: "100%", background: "#FFFFFF", border: "1px solid #CBD5E1",
                  borderRadius: "8px", padding: "14px 16px", color: "#1E293B",
                  fontFamily: "'Courier New', monospace", fontSize: "13px",
                  resize: "vertical", lineHeight: "1.6", boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontSize: "11px", color: "#475569", marginBottom: "8px", letterSpacing: "0.1em", fontWeight: "600" }}>OR UPLOAD A .TXT FILE</div>
              <div
                onClick={() => textFileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = "#3B82F6"; }}
                onDragLeave={(e) => { e.currentTarget.style.borderColor = "#CBD5E1"; }}
                onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = "#CBD5E1"; handleTextFileChange(e.dataTransfer.files[0]); }}
                style={{
                  border: "2px dashed #CBD5E1", borderRadius: "10px", padding: "20px",
                  textAlign: "center", cursor: "pointer", background: "#F8FAFC", transition: "border-color 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "#94A3B8"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "#CBD5E1"}
              >
                <input ref={textFileInputRef} type="file" accept=".txt,.text" style={{ display: "none" }} onChange={(e) => handleTextFileChange(e.target.files[0])} />
                <div style={{ fontSize: "20px", marginBottom: "6px" }}>📄</div>
                <div style={{ color: "#475569", fontSize: "12px" }}>Click to upload .txt file</div>
              </div>
            </div>
            <button
              onClick={handleTextSubmit} disabled={!textInput.trim()}
              style={{
                width: "100%", padding: "14px",
                background: textInput.trim() ? "linear-gradient(135deg, #3B82F6, #8B5CF6)" : "#E2E8F0",
                border: "none", borderRadius: "10px",
                color: textInput.trim() ? "#fff" : "#94A3B8",
                cursor: textInput.trim() ? "pointer" : "not-allowed",
                fontFamily: "inherit", fontSize: "14px", letterSpacing: "0.1em", fontWeight: "600",
              }}
            >
              {textInput.trim() ? "📝 USE THIS TRANSCRIPT" : "PASTE OR UPLOAD A TRANSCRIPT FIRST"}
            </button>
          </div>
        )}

        {/* Video Mode */}
        {mode === "video" && !isVideoProcessing && status !== "done" && (
          <div>
            <LanguageSelector />
            <div
              onDragOver={(e) => { e.preventDefault(); setVideoDragOver(true); }}
              onDragLeave={() => setVideoDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setVideoDragOver(false); handleVideoFileChange(e.dataTransfer.files[0]); }}
              onClick={() => videoFileInputRef.current?.click()}
              style={{
                border: `2px dashed ${videoDragOver ? "#10B981" : videoFile ? "#22C55E" : "#CBD5E1"}`,
                borderRadius: "12px", padding: "48px 24px", textAlign: "center", cursor: "pointer",
                background: videoDragOver ? "rgba(16,185,129,0.08)" : "#F8FAFC",
                transition: "all 0.2s", marginBottom: "20px",
              }}
            >
              <input ref={videoFileInputRef} type="file" accept="video/*,.mp4,.mov,.webm,.mkv,.avi,.m4v" style={{ display: "none" }} onChange={(e) => handleVideoFileChange(e.target.files[0])} />
              {videoFile ? (
                <>
                  <div style={{ fontSize: "32px", marginBottom: "12px" }}>✅</div>
                  <div style={{ color: "#15803D", fontWeight: "600", marginBottom: "4px" }}>{videoFile.name}</div>
                  <div style={{ color: "#64748B", fontSize: "12px" }}>{(videoFile.size / 1024 / 1024).toFixed(1)} MB — Click to change</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: "32px", marginBottom: "12px" }}>🎬</div>
                  <div style={{ color: "#475569", marginBottom: "4px" }}>Drop video file here or click to browse</div>
                  <div style={{ color: "#64748B", fontSize: "12px" }}>MP4, MOV, WebM, MKV, AVI, M4V supported</div>
                  <div style={{ color: "#94A3B8", fontSize: "11px", marginTop: "8px" }}>Audio is extracted server-side — video is never stored permanently</div>
                </>
              )}
            </div>
            {error && <div style={errorStyle}>⚠️ {error}</div>}
            <button
              onClick={uploadVideoAndTranscribe} disabled={!videoFile}
              style={{
                width: "100%", padding: "14px",
                background: videoFile ? "linear-gradient(135deg, #10B981, #059669)" : "#E2E8F0",
                border: "none", borderRadius: "10px",
                color: videoFile ? "#fff" : "#94A3B8",
                cursor: videoFile ? "pointer" : "not-allowed",
                fontFamily: "inherit", fontSize: "14px", letterSpacing: "0.1em", fontWeight: "600", transition: "all 0.2s",
              }}
            >
              {videoFile ? "🎬 UPLOAD & TRANSCRIBE VIDEO" : "SELECT A VIDEO FILE FIRST"}
            </button>
          </div>
        )}

        {/* Upload / Record modes */}
        {mode !== "history" && mode !== "text" && mode !== "video" && (
          <>
            {(status === "idle" || status === "error") && (
              <>
                <LanguageSelector />

                {/* Upload Mode */}
                {mode === "upload" && (
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileChange(e.dataTransfer.files[0]); }}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: `2px dashed ${dragOver ? "#3B82F6" : file ? "#22C55E" : "#CBD5E1"}`,
                      borderRadius: "12px", padding: "48px 24px", textAlign: "center", cursor: "pointer",
                      background: dragOver ? "rgba(59,130,246,0.08)" : "#F8FAFC",
                      transition: "all 0.2s", marginBottom: "20px",
                    }}
                  >
                    <input ref={fileInputRef} type="file" accept="audio/*" style={{ display: "none" }} onChange={(e) => handleFileChange(e.target.files[0])} />
                    {file ? (
                      <>
                        <div style={{ fontSize: "32px", marginBottom: "12px" }}>✅</div>
                        <div style={{ color: "#15803D", fontWeight: "600", marginBottom: "4px" }}>{file.name}</div>
                        <div style={{ color: "#64748B", fontSize: "12px" }}>{(file.size / 1024 / 1024).toFixed(2)} MB — Click to change</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: "32px", marginBottom: "12px" }}>🎵</div>
                        <div style={{ color: "#475569", marginBottom: "4px" }}>Drop audio file here or click to browse</div>
                        <div style={{ color: "#64748B", fontSize: "12px" }}>MP3, WAV, M4A, FLAC, WebM supported</div>
                      </>
                    )}
                  </div>
                )}

                {/* Record Mode */}
                {mode === "record" && (
                  <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "40px 24px", textAlign: "center", marginBottom: "20px", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
                    {!isRecording && !file && (
                      <div style={{ marginBottom: "24px", textAlign: "left" }}>
                        <div style={{ fontSize: "11px", color: "#475569", marginBottom: "8px", letterSpacing: "0.1em", fontWeight: "600" }}>MICROPHONE</div>
                        <select value={selectedDeviceId} onChange={(e) => setSelectedDeviceId(e.target.value)} style={{ ...selectStyle, background: "#F8FAFC" }}>
                          <option value="">Default microphone</option>
                          {audioDevices.map((device, i) => (
                            <option key={device.deviceId} value={device.deviceId}>{getDeviceLabel(device, i)}</option>
                          ))}
                        </select>
                        {audioDevices.every((d) => !d.label) && (
                          <div style={{ fontSize: "11px", color: "#64748B", marginTop: "6px" }}>Device names appear after microphone permission is granted.</div>
                        )}
                      </div>
                    )}
                    {isRecording ? (
                      <>
                        <div style={{
                          width: "72px", height: "72px", borderRadius: "50%",
                          background: "rgba(239,68,68,0.12)", border: "2px solid #EF4444",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          margin: "0 auto 16px", animation: "pulse 1s infinite", fontSize: "28px",
                        }}>🔴</div>
                        <div style={{ color: "#DC2626", fontWeight: "700", fontSize: "24px", marginBottom: "8px" }}>
                          {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, "0")}
                        </div>
                        <div style={{ color: "#475569", fontSize: "12px", marginBottom: "20px" }}>Recording in progress...</div>
                        <button onClick={stopRecording} style={{ background: "#EF4444", border: "none", borderRadius: "8px", padding: "12px 28px", color: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: "13px", letterSpacing: "0.08em" }}>
                          ⏹ STOP RECORDING
                        </button>
                      </>
                    ) : file ? (
                      <>
                        <div style={{ fontSize: "32px", marginBottom: "12px" }}>✅</div>
                        <div style={{ color: "#15803D", marginBottom: "16px" }}>Recording saved — {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, "0")}</div>
                        <button onClick={() => { setFile(null); setAudioUrl(null); setRecordingTime(0); }} style={{ background: "transparent", border: "1px solid #CBD5E1", borderRadius: "8px", padding: "10px 20px", color: "#475569", cursor: "pointer", fontFamily: "inherit", fontSize: "12px" }}>
                          Record Again
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: "48px", marginBottom: "16px" }}>🎤</div>
                        <div style={{ color: "#475569", marginBottom: "20px" }}>Click to start recording</div>
                        <button onClick={startRecording} style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6)", border: "none", borderRadius: "8px", padding: "12px 28px", color: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: "13px", letterSpacing: "0.08em" }}>
                          ▶ START RECORDING
                        </button>
                      </>
                    )}
                  </div>
                )}

                {audioUrl && (
                  <div style={{ marginBottom: "20px" }}>
                    <audio controls src={audioUrl} style={{ width: "100%", borderRadius: "8px" }} />
                  </div>
                )}

                {error && <div style={errorStyle}>⚠️ {error}</div>}

                <button
                  onClick={uploadAndTranscribe} disabled={!file}
                  style={{
                    width: "100%", padding: "14px",
                    background: file ? "linear-gradient(135deg, #3B82F6, #8B5CF6)" : "#E2E8F0",
                    border: "none", borderRadius: "10px", color: file ? "#fff" : "#94A3B8",
                    cursor: file ? "pointer" : "not-allowed", fontFamily: "inherit",
                    fontSize: "14px", letterSpacing: "0.1em", fontWeight: "600", transition: "all 0.2s",
                  }}
                >
                  {file ? "⚡ TRANSCRIBE AUDIO" : "SELECT AUDIO FIRST"}
                </button>
              </>
            )}
          </>
        )}

        {/* Processing State */}
        {isProcessing && (
          <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "16px", padding: "48px 32px", textAlign: "center", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
            <div style={{ fontSize: "48px", marginBottom: "24px" }}>
              {mode === "video" ? (videoStatus === "uploading" ? "⬆️" : "🎬") : status === "uploading" ? "⬆️" : "⚙️"}
            </div>
            <div style={{ color: "#0F172A", fontWeight: "600", fontSize: "18px", marginBottom: "8px" }}>
              {mode === "video" ? videoStatusLabel : status === "uploading" ? "Uploading audio..." : "Processing transcript..."}
            </div>
            <div style={{ color: "#475569", fontSize: "13px", marginBottom: "32px" }}>
              {mode === "video" ? videoStatusSub : status === "processing" ? "Speaker detection · Timestamps · Language analysis" : "Sending to secure storage"}
            </div>
            <div style={{ height: "4px", background: "#E2E8F0", borderRadius: "99px", overflow: "hidden", maxWidth: "400px", margin: "0 auto" }}>
              <div style={{
                height: "100%",
                width: mode === "video" ? (videoStatus === "uploading" ? `${videoProgress}%` : "60%") : `${progress}%`,
                background: mode === "video" ? "linear-gradient(90deg, #10B981, #059669)" : "linear-gradient(90deg, #3B82F6, #8B5CF6)",
                borderRadius: "99px",
                transition: (mode === "video" && videoStatus === "extracting") || status === "processing" ? "none" : "width 0.5s ease",
                animation: (mode === "video" && videoStatus === "extracting") || status === "processing" ? "shimmer 2s ease-in-out infinite" : "none",
              }} />
            </div>
            <div style={{ color: "#64748B", fontSize: "12px", marginTop: "12px" }}>
              {mode === "video" ? (videoStatus === "uploading" ? `${videoProgress}%` : "Extracting audio...") : status === "processing" ? "Transcribing via AssemblyAI..." : `${progress}%`}
            </div>
          </div>
        )}

        {/* Done State */}
        {status === "done" && transcript && (
          <>
            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "24px" }}>
              {[
                { label: "DURATION", value: transcript.audio_duration ? `${Math.floor(transcript.audio_duration / 60)}m ${transcript.audio_duration % 60}s` : "—" },
                { label: "SPEAKERS", value: transcript.utterances ? new Set(transcript.utterances.map(u => u.speaker)).size : "—" },
                { label: "WORDS", value: transcript.words?.length?.toLocaleString() || "—" },
              ].map((s) => (
                <div key={s.label} style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "16px", textAlign: "center", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
                  <div style={{ fontSize: "22px", fontWeight: "700", color: "#0F172A", marginBottom: "4px" }}>{s.value}</div>
                  <div style={{ fontSize: "10px", color: "#64748B", letterSpacing: "0.1em", fontWeight: "600" }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Tab Toggle */}
            <div style={{ display: "flex", gap: "4px", background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "4px", marginBottom: "20px", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
              {["full", "speakers"].map((t) => (
                <button key={t} onClick={() => setActiveTab(t)} style={{
                  flex: 1, padding: "9px", borderRadius: "7px", border: "none", cursor: "pointer",
                  fontSize: "12px", letterSpacing: "0.08em", fontFamily: "inherit",
                  fontWeight: activeTab === t ? "700" : "600",
                  background: activeTab === t ? "linear-gradient(135deg, #3B82F6, #8B5CF6)" : "transparent",
                  color: activeTab === t ? "#fff" : "#475569", transition: "all 0.2s",
                }}>
                  {t === "full" ? "📄 FULL TRANSCRIPT" : "👥 BY SPEAKER"}
                </button>
              ))}
            </div>

            {/* Transcript Content */}
            <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "24px", maxHeight: "440px", overflowY: "auto", marginBottom: "20px", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
              {activeTab === "full" ? (
                wordGroups.length > 0 ? wordGroups.map((group, gi) => (
                  <div key={gi} style={{ marginBottom: "12px", display: "flex", gap: "12px" }}>
                    <div style={{ fontSize: "11px", color: "#64748B", minWidth: "44px", paddingTop: "2px" }}>
                      {formatTime(group[0]?.start || 0)}
                    </div>
                    <div style={{
                      color: "#1E293B", lineHeight: "1.7", fontSize: "14px",
                      direction: isRTL ? "rtl" : "ltr",
                      textAlign: isRTL ? "right" : "left",
                    }}>
                      {group.map((w) => w.text).join(" ")}
                    </div>
                  </div>
                )) : (
                  <div style={{
                    color: "#1E293B", lineHeight: "1.8", fontSize: "14px",
                    direction: isRTL ? "rtl" : "ltr",
                    textAlign: isRTL ? "right" : "left",
                  }}>
                    {transcript.text}
                  </div>
                )
              ) : (
                groupBySpeaker(transcript.utterances).map((u, i) => (
                  <div key={i} style={{ marginBottom: "20px", display: "flex", gap: "14px" }}>
                    <div style={{ minWidth: "60px", paddingTop: "2px" }}>
                      <div style={{ fontSize: "10px", fontWeight: "700", color: getSpeakerColor(u.speaker), marginBottom: "4px" }}>{u.speaker}</div>
                      <div style={{ fontSize: "10px", color: "#64748B" }}>{formatTime(u.start)}</div>
                    </div>
                    <div style={{ flex: 1, borderLeft: `2px solid ${getSpeakerColor(u.speaker)}`, paddingLeft: "14px" }}>
                      <div style={{
                        color: "#1E293B", lineHeight: "1.7", fontSize: "14px",
                        direction: isRTL ? "rtl" : "ltr",
                        textAlign: isRTL ? "right" : "left",
                      }}>
                        {u.text}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => navigator.clipboard.writeText(transcript.text)} style={actionBtnStyle}>📋 COPY</button>
              <button
                onClick={() => {
                  const blob = new Blob([transcript.text], { type: "text/plain" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = "transcript.txt";
                  a.click();
                }}
                style={actionBtnStyle}
              >
                ⬇️ TXT
              </button>
              {savedEntry && (
                <button onClick={() => deleteTranscription(savedEntry.id, savedEntry.storagePath).then(reset)} style={{ ...actionBtnStyle, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)", color: "#B91C1C" }}>
                  🗑 DELETE
                </button>
              )}
              <button onClick={reset} style={{ flex: 1, padding: "12px", background: "linear-gradient(135deg, #3B82F6, #8B5CF6)", border: "none", borderRadius: "8px", color: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: "12px", letterSpacing: "0.08em" }}>
                + NEW
              </button>
            </div>

            {/* Generate Summary */}
            <button
              onClick={() => setShowSummary(true)}
              style={{ width: "100%", padding: "14px", marginTop: "10px", background: "linear-gradient(135deg, #C8973A, #F59E0B)", border: "none", borderRadius: "10px", color: "#fff", cursor: "pointer", fontFamily: "inherit", fontSize: "14px", letterSpacing: "0.1em", fontWeight: "600" }}
            >
              ✨ GENERATE LESSON SUMMARY
            </button>

            {showSummary && (
              <LessonSummaryModal
                transcript={transcript.text}
                language={languagePayload}
                onClose={() => setShowSummary(false)}
                onSave={(summary) => savedEntry && saveSummary(savedEntry.id, summary)}
              />
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.05); } }
        @keyframes shimmer { 0%, 100% { width: 20%; margin-left: 0%; } 50% { width: 60%; margin-left: 20%; } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 3px; }
        select option { background: #FFFFFF; color: #1E293B; }
      `}</style>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────
const selectStyle = {
  background: "#FFFFFF", border: "1px solid #CBD5E1",
  borderRadius: "8px", padding: "10px 14px", color: "#1E293B",
  fontFamily: "'Courier New', monospace", fontSize: "13px",
  cursor: "pointer", flex: 1,
};

const errorStyle = {
  background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)",
  borderRadius: "8px", padding: "12px 16px", color: "#B91C1C",
  fontSize: "13px", marginBottom: "16px",
};

const actionBtnStyle = {
  flex: 1, padding: "12px", background: "#F1F5F9",
  border: "1px solid #CBD5E1", borderRadius: "8px",
  color: "#334155", cursor: "pointer", fontFamily: "inherit",
  fontSize: "12px", letterSpacing: "0.08em",
};